/**
 * Text formatting utilities
 */

/**
 * Convert hyphenated or underscored strings to title case
 */
export function humanize(str: string): string {
  if (!str || typeof str !== "string") return "";
  const replaced = str.replace(/[-_]/g, " ").trim();
  if (!replaced || !/\w/.test(replaced)) return "";
  return replaced.replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Pluralize a string using common English rules
 */
function pluralize(str: string): string {
  const specialPlurals: Record<string, string> = {
    library: "Libraries",
    repository: "Repositories",
    policy: "Policies",
    proxy: "Proxies",
    api: "APIs",
    data: "Data",
    analysis: "Analyses",
    schema: "Schemas",
  };

  const lower = str.toLowerCase();
  if (specialPlurals[lower]) {
    return specialPlurals[lower];
  }

  // Handle words ending in 'y' preceded by consonant
  if (
    str.endsWith("y") &&
    !["ay", "ey", "iy", "oy", "uy"].some((v) => str.endsWith(v))
  ) {
    return `${str.slice(0, -1)}ies`;
  }

  // Handle words ending in s, x, ch, sh
  if (
    str.endsWith("s") ||
    str.endsWith("x") ||
    str.endsWith("ch") ||
    str.endsWith("sh")
  ) {
    return `${str}es`;
  }

  // Default: just add 's'
  return `${str}s`;
}

/**
 * Format a catalog entity matcher (kind/type) into a display title
 */
export function formatTitle(matcher: { kind: string; type?: string }): string {
  if (matcher.type) {
    // Special cases for types with specific formatting
    const specialCases: Record<string, string> = {
      "ml-model": "ML Models",
      "data-pipeline": "Data Pipelines",
      "mobile-app": "Mobile Apps",
      api: "APIs",
      grpc: "gRPC Services",
      graphql: "GraphQL APIs",
      etl: "ETL Pipelines",
    };

    if (specialCases[matcher.type]) {
      return specialCases[matcher.type];
    }

    return pluralize(humanize(matcher.type));
  }

  // Special cases for kinds with specific formatting
  const kindPlurals: Record<string, string> = {
    API: "APIs",
    System: "Systems",
    Domain: "Domains",
    Component: "Components",
    Resource: "Resources",
    Group: "Groups",
    User: "Users",
    Template: "Templates",
    Location: "Locations",
  };

  return kindPlurals[matcher.kind] || pluralize(matcher.kind);
}
